import React, { type ComponentProps, useCallback, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { Pressable, type PressableStateCallbackType } from 'react-native-gesture-handler';

import { theme } from '@garnish/constants';

import { BodyText, CloudinaryImage, useHovered, useResponsive, webOnlyStyles } from '@sg/garnish';

import { ReorderRailCardStatusIcon } from '../ReorderRailCardStatusIcon';
import { ReorderRailCardStatusOverlay } from '../ReorderRailCardStatusOverlay';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderRailCard = (props: Props) => {
  const {
    lineItemName,
    imageUrl,
    lastOrderedAt,
    accessibilityLabel,
    disabled,
    status = 'idle',
    onPress,
  } = props;

  const { formatDate } = useIntl();
  const { match, minWidth } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const ref = useRef(null);
  const isHovered = useHovered(ref);

  // ─────────────────────────────────────────────────────────────────────

  const date = formatDate(new Date(lastOrderedAt), { month: 'numeric', day: 'numeric' });

  // NOTE: On SM+ the overlay is shown on hover, and also while an add is in
  //       flight or just completed so the feedback is visible without hover.
  const isOverlayVisible = isHovered || status !== 'idle';

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = match([styles.containerXS, styles.containerSM]);

  const pressableStyles = useMemo(
    () => [styles.pressable, match([styles.pressableXS, styles.pressableSM]), pressableWebStyles],
    [match],
  );

  const imageTileStyles = [
    styles.imageTile,
    match([styles.imageTileXS, styles.imageTileSM]),
    imageTileWebStyles,
    isHovered ? styles.imageTileHovered : undefined,
  ];

  const imageStyles = [styles.image, imageWebStyles, isHovered ? styles.imageHovered : undefined];

  // ─── Handlers ────────────────────────────────────────────────────────

  const getPressableStyles = useCallback(
    (state: PressableStateCallbackType) => {
      if (!state.pressed) return pressableStyles;

      return [...pressableStyles, styles.pressablePressed];
    },
    [pressableStyles],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View ref={ref} style={containerStyles}>
      <Pressable
        style={getPressableStyles}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? lineItemName}
        disabled={disabled}
        onPress={onPress}
      >
        <View style={imageTileStyles}>
          {imageUrl ? (
            <CloudinaryImage
              baseUrl={imageUrl}
              config={IMAGE_CONFIG}
              style={imageStyles}
              alt={lineItemName}
            />
          ) : null}

          {minWidth.isSM ? (
            <ReorderRailCardStatusOverlay status={status} visible={isOverlayVisible} />
          ) : (
            <ReorderRailCardStatusIcon status={status} />
          )}
        </View>

        <View>
          <BodyText sizeMatch={['12', '16']} bold numberOfLines={2}>
            {lineItemName}
          </BodyText>

          <BodyText sizeMatch={['12']} style={styles.subtitle}>
            <FormattedMessage {...messages.lastOrdered} values={{ date }} />
          </BodyText>
        </View>
      </Pressable>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  lastOrdered: {
    defaultMessage: 'Last ordered {date}',
    description: 'Menu | Reorder rail | Card | Last ordered date',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_SIZE_XS = 120;
const CARD_SIZE_SM = 192;

const IMAGE_CONFIG: ImageConfig = [
  { crop: 'crop', width: 0.95, height: 0.95 },
  { width: Math.round(CARD_SIZE_SM * 1.2) },
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['1'],
  },
  containerSM: {
    paddingHorizontal: theme.spacing['3'],
  },
  pressable: {
    flex: 1,
    gap: theme.spacing['4'],
    borderRadius: theme.radius.medium,
    marginVertical: theme.spacing['3'],
  },
  pressableXS: {
    width: CARD_SIZE_XS,
  },
  pressableSM: {
    width: CARD_SIZE_SM,
  },
  pressablePressed: {
    opacity: 0.7,
  },
  imageTile: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: 10,
    overflow: 'hidden',
  },
  imageTileXS: {
    height: CARD_SIZE_XS,
  },
  imageTileSM: {
    height: CARD_SIZE_SM,
  },
  imageTileHovered: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageHovered: {
    transform: [{ scale: 1.05 }],
  },
  subtitle: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

const pressableWebStyles = webOnlyStyles({
  cursor: 'pointer',
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['2'],
});
const imageTileWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms`,
});
const imageWebStyles = webOnlyStyles({
  transition: `transform ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  lineItemName: string;
  lastOrderedAt: string | number | Date;
  onPress: () => void;
  imageUrl?: string;
  accessibilityLabel?: string;
  disabled?: boolean;
  status?: ComponentProps<typeof ReorderRailCardStatusIcon>['status'];
};

type ImageConfig = ComponentProps<typeof CloudinaryImage>['config'];
